import { Component, Input, OnInit } from '@angular/core';
import { DialogContentBase, DialogRef } from '@progress/kendo-angular-dialog';
import {
  faCarSide,
  faChalkboardUser,
  faCircleCheck,
  faCircleXmark,
  faClock,
  faIndent,
  faLocationDot,
  faRepeat,
  faStairs,
  faUsers,
  IconDefinition,
} from '@fortawesome/free-solid-svg-icons';
import { SVGIcon } from '@progress/kendo-svg-icons';
import { ReferenceStore } from 'src/app/stores/reference.store';
import { combineLatest, finalize } from 'rxjs';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import {
  IIndirectServiceMeetingDetails,
  IProviderMeetingJson,
} from '../../interfaces/indirect-service-meeting';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { LoadingService } from 'src/app/services/loading.service';
import { EncounterAttendenceService } from 'src/app/modules/provider-management/services/encounter-attendence.service';
import { IProviderMeetingDetails } from 'src/app/modules/provider-management/interfaces/pa-meeting-details';
import { IndirectMeetingRequest } from '../../models/indirect-meeting-request';
import { NotificationService } from '@progress/kendo-angular-notification';
import {
  dialogAnimationDuration,
  dialogTimer,
} from '../../constants/dialog-specs';
import moment from 'moment';
import { SessionType } from '../../enums/session-type';
import { StudentService } from 'src/app/services/student.service';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { referenceDataFilterSettings } from '../../constants/reference-data-filter-settings';

@Component({
  selector: 'app-indirect-service',
  templateUrl: './indirect-service.component.html',
  styleUrls: ['./indirect-service.component.scss'],
  standalone: false,
})
export class IndirectServiceComponent
  extends DialogContentBase
  implements OnInit
{
  public chalkBordUser: SVGIcon = this.customFaIcon(faChalkboardUser);
  public clock: SVGIcon = this.customFaIcon(faClock);
  public repeat: SVGIcon = this.customFaIcon(faRepeat);
  public users: SVGIcon = this.customFaIcon(faUsers);
  public locationDot: SVGIcon = this.customFaIcon(faLocationDot);
  public stairs: SVGIcon = this.customFaIcon(faStairs);
  public car: SVGIcon = this.customFaIcon(faCarSide);
  public indent: SVGIcon = this.customFaIcon(faIndent);
  public xMark: SVGIcon = this.customFaIcon(faCircleXmark);
  public checkMark: SVGIcon = this.customFaIcon(faCircleCheck);
  public sessionTypes: IReferenceData[] = [];
  public indirectServiceMeetingDetails: IIndirectServiceMeetingDetails;
  public indirectServiceForm: FormGroup;
  public isScheduledTab: boolean = false;
  public meetingSk: string;
  public providerSk: string;
  public viewMode: boolean = false;
  public encounterSk: string;
  public scheduledMeetingData: IIndirectServiceMeetingDetails = {};
  public actualMeetingData: IIndirectServiceMeetingDetails = {};
  public minTime: Date = new Date();
  public maxTime: Date = new Date();
  public attendeeList: string[] = [];
  public sessionTypeEnum = SessionType;
  @Input() displayParticipantsAndLocatiion: boolean = true;
  studentsList: any[];
  filterSettings: DropDownFilterSettings = referenceDataFilterSettings;

  constructor(
    dialog: DialogRef,
    public referenceStore: ReferenceStore,
    private formBuilder: FormBuilder,
    // private providerService: ProviderManagementService,
    private laodingService: LoadingService,
    private encounterService: EncounterAttendenceService,
    private notificationService: NotificationService,
    public studentService: StudentService,
  ) {
    super(dialog);
    this.initializeForm();
  }
  ngOnInit(): void {
    this.minTime.setHours(6, 0, 0, 0);   // 6:00 AM
    this.maxTime.setHours(22, 0, 0, 0);  // 10:00 PM

    this.laodingService.show();
    this.getReferenceData();
    //this.updateFilteredSessionTypes(); 
    this.getPAMeetingDetails();
  }

  initializeForm(): FormGroup {
    return (this.indirectServiceForm = this.formBuilder.group({
      sessionType: new FormControl(null),
      startDate: new FormControl(null),
      startTime: new FormControl(null),
      endTime: new FormControl(null),
      locationType: new FormControl(null),
      location: new FormControl(null),
      floorRoom: new FormControl(null),
      teamsMeeting: new FormControl(false),
      additionalInfo: new FormControl(null),
      students: new FormControl([]),
      requiredAttendees: new FormControl([]),
      optionalAttendees: new FormControl([]),
      trainingDescription: new FormControl(),
    }));
  }

  private normalizeAttendees(attendees: any): string[] {
    if (!attendees) {
      return [];
    }
    if (Array.isArray(attendees) && typeof attendees[0] === 'string') {
      return attendees as string[];
    }
    if (Array.isArray(attendees)) {
      return attendees
        .map((a: any) => a?.text || a?.email || a?.id)
        .filter((x: any) => !!x);
    }
    return [];
  }

  patchForm(meetingData: IIndirectServiceMeetingDetails): void {
    const formValue: any = {
      sessionType: meetingData?.sessionType || null,
      locationType: meetingData?.locationType || null,
      location: meetingData?.location || null,
      floorRoom: meetingData?.floorRoom || null,
      teamsMeeting: meetingData?.teamsMeeting || false,
      additionalInfo: meetingData?.additionalInfo || null,
      students: meetingData?.students || [],
      requiredAttendees: this.normalizeAttendees(meetingData?.requiredAttendees),
      optionalAttendees: this.normalizeAttendees(meetingData?.optionalAttendees),
      trainingDescription: meetingData?.trainingDescription || null,
    };
    formValue.sessionType = this.fixSessionTypeInValue(meetingData?.sessionType);

    if (meetingData?.startDate) {
      formValue.startDate = this.toLocalDateTime(meetingData.startDate);
    }
    if (meetingData?.startTime) {
      formValue.startTime = this.toLocalDateTime(meetingData.startTime);
    }
    if (meetingData?.endTime) {
      formValue.endTime = this.toLocalDateTime(meetingData.endTime);
    }  
    
    this.studentsList = [meetingData?.students];

    this.indirectServiceForm.patchValue(formValue);
  }

  private updateFilteredSessionTypes(): void {
  if (this.referenceStore.sessionType$) {
    // filter out sessionType 1 for indirect services
    combineLatest([this.referenceStore.sessionType$]).subscribe(
      ([sessionType]: [IReferenceData[]]) => {
        this.sessionTypes = sessionType.filter(type => type.id !== 1);
      }
    );
  }
}

  private toLocalDateTime(
    value: string | Date | null | undefined,
  ): Date | null {
    if (!value) {
      return null;
    }

    if (value instanceof Date) {
      return value;
    }

    const parsed = moment.parseZone(value);
    if (parsed.isValid()) {
      return parsed.local(true).toDate();
    }

    const fallback = new Date(value);
    return Number.isNaN(fallback.getTime()) ? null : fallback;
  }

  get durationLabel(): string {
    const startDate: Date | null = this.indirectServiceForm?.get('startDate')
      ?.value;
    const startTime: Date | null = this.indirectServiceForm?.get('startTime')
      ?.value;
    const endTime: Date | null = this.indirectServiceForm?.get('endTime')
      ?.value;

    if (!startDate || !startTime || !endTime) {
      return '--';
    }

    const start = new Date(startDate);
    start.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

    const end = new Date(startDate);
    end.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

    const diffMs: number = end.getTime() - start.getTime();
    if (Number.isNaN(diffMs)) {
      return '--';
    }

    const totalMinutes: number = Math.floor(Math.abs(diffMs) / 60000);
    const hours: number = Math.floor(totalMinutes / 60);
    const minutes: number = totalMinutes % 60;

    const isNegative: boolean = diffMs < 0;
    const sign: string = isNegative ? '-' : '';

    if (hours === 0) {
      return `${sign}${minutes}m`;
    }
    return `${sign}${hours}h${minutes}m`;
  }

  get hasInvalidTimeRange(): boolean {
    const startDate: Date | null = this.indirectServiceForm?.get('startDate')
      ?.value;
    const startTime: Date | null = this.indirectServiceForm?.get('startTime')
      ?.value;
    const endTime: Date | null = this.indirectServiceForm?.get('endTime')
      ?.value;

    if (!startDate || !startTime || !endTime) {
      return false;
    }

    const start = new Date(startDate);
    start.setHours(startTime.getHours(), startTime.getMinutes(), 0, 0);

    const end = new Date(startDate);
    end.setHours(endTime.getHours(), endTime.getMinutes(), 0, 0);

    return end.getTime() < start.getTime();
  }

  getPAMeetingDetails(): void {
    this.scheduledMeetingData = {};
    this.actualMeetingData = {};
    this.encounterService
      .getPAMeeting(this.meetingSk)
      .pipe(finalize(() => this.laodingService.hide()))
      .subscribe({
        next: (res: IProviderMeetingDetails) => {
          if (res) {
            const parsedFormJson: IProviderMeetingJson | any = JSON.parse(
              res.MeetingJson,
            );
            const actualData: IIndirectServiceMeetingDetails =
              parsedFormJson.actualMeetingData ||
              parsedFormJson.scheduleMeetingData ||
              {};
            const scheduleData: IIndirectServiceMeetingDetails =
              parsedFormJson.scheduleMeetingData || {};
            // let meetingData: IIndirectServiceMeetingDetails;

              if(!this.encounterSk){
              this.scheduledMeetingData = {
              sessionType: parsedFormJson.sessionType,
              startDate:parsedFormJson.startDate,
              startTime: parsedFormJson.startTime,
              endTime: parsedFormJson.endTime,
              locationType:
                parsedFormJson.locationType,
              location: parsedFormJson.location,
              floorRoom: parsedFormJson.floorRoom,
              teamsMeeting:
                parsedFormJson.teamsMeeting ?? false,
              additionalInfo:
                parsedFormJson.additionalInfo || null,
              students: parsedFormJson.students || [],
              requiredAttendees:
                parsedFormJson.requiredAttendees || [],
              optionalAttendees:
                parsedFormJson.optionalAttendees || [],
              trainingDescription:
                parsedFormJson.trainingDescription ||
                '',
            };

            this.scheduledMeetingData.sessionType = this.fixSessionTypeInValue(
                this.scheduledMeetingData.sessionType
              );

            this.actualMeetingData = {
              sessionType: parsedFormJson.sessionType,
              startDate:parsedFormJson.startDate,
              startTime: parsedFormJson.startTime,
              endTime: parsedFormJson.endTime,
              locationType:
                parsedFormJson.locationType,
              location: parsedFormJson.location,
              floorRoom: parsedFormJson.floorRoom,
              teamsMeeting:
                parsedFormJson.teamsMeeting ?? false,
              additionalInfo:
                parsedFormJson.additionalInfo || null,
              students: parsedFormJson.students || [],
              requiredAttendees:
                parsedFormJson.requiredAttendees || [],
              optionalAttendees:
                parsedFormJson.optionalAttendees || [],
              trainingDescription:
                parsedFormJson.trainingDescription ||
                '',
            };
              }else{
                this.scheduledMeetingData = {
                  sessionType: scheduleData.sessionType,
                  startDate: scheduleData.startDate,
              startTime: scheduleData.startTime,
              endTime: scheduleData.endTime,
              locationType:
                scheduleData.locationType,
                location: scheduleData.location,
              floorRoom: scheduleData.floorRoom,
              teamsMeeting:
              scheduleData.teamsMeeting ?? false,
              additionalInfo:
                scheduleData.additionalInfo || actualData.additionalInfo,
              students: scheduleData.students || [],
              requiredAttendees:
              scheduleData.requiredAttendees ||
                [],
                optionalAttendees:
                scheduleData.optionalAttendees ||
                [],
                trainingDescription:
                scheduleData.trainingDescription,
              };
             if (!this.scheduledMeetingData.sessionType && actualData.sessionType) {
                this.scheduledMeetingData.sessionType = actualData.sessionType;
              }

              this.scheduledMeetingData.sessionType = this.fixSessionTypeInValue(
                this.scheduledMeetingData.sessionType
              );

              this.actualMeetingData = {
                  sessionType: actualData.sessionType,
                  startDate: actualData.startDate,
              startTime: actualData.startTime,
              endTime: actualData.endTime,
              locationType:
                actualData.locationType,
                location: actualData.location,
              floorRoom: actualData.floorRoom,
              teamsMeeting:
              actualData.teamsMeeting ?? false,
              additionalInfo:
                actualData.additionalInfo,
              students: actualData.students || [],
              requiredAttendees:
              actualData.requiredAttendees ||
                [],
                optionalAttendees:
                actualData.optionalAttendees ||
                [],
                trainingDescription:
                actualData.trainingDescription
              };
            }
            this.indirectServiceMeetingDetails = scheduleData;
            this.isScheduledTab = false;
            this.patchForm(this.actualMeetingData);
          }
        },
        error: () => {
          this.laodingService.hide();
        }
      });
  }

  get currentSessionType(): IReferenceData | null {
  return this.indirectServiceForm.get('sessionType')?.value;
}

  get showStudents(): boolean {
    const st = this.currentSessionType;
    return !this.isScheduledTab && !!st && st.id === 2;
  }

  get showTraining(): boolean {
    const st = this.currentSessionType;
    return !this.isScheduledTab && !!st && st.id === 3;
  }

  get showParticipantsAndLocation(): boolean {
    const st = this.currentSessionType;
    const id = st?.id as number | undefined;
    return (
      this.isScheduledTab === false &&
      id !== null &&
      ![6, 7, 8, 9].includes(id) && this.displayParticipantsAndLocatiion
    );
  }

  private fixSessionTypeInValue(sessionType: any): IReferenceData | null {
    if (!sessionType) return null;

    if (typeof sessionType === 'object' && sessionType.id !== null) {
      const obj = this.sessionTypes.find(st => st.id === sessionType.id);
      return obj || null;
    }

    if (typeof sessionType === 'number') {
      const obj = this.sessionTypes.find(st => st.id === sessionType);
      return obj || null;
    }

    if (typeof sessionType === 'string') {
      const num = Number(sessionType);
      if (!Number.isNaN(num)) {
        const obj = this.sessionTypes.find(st => st.id === num);
        return obj || null;
      }
    }

    return null;
  }

   selectTab(tab: 'scheduled' | 'actual'): void {
    this.isScheduledTab = tab === 'scheduled';

    const targetData =
      tab === 'scheduled' ? this.scheduledMeetingData : this.actualMeetingData;

    if (targetData) {
      this.patchForm(targetData);
    }
  }

  customFaIcon(icon: IconDefinition): SVGIcon {
    // FontAwesome icon structure: [width, height, horizontalAlign, verticalAlign, pathData]
    if (!icon || !icon.icon) {
      return {
        name: 'unknown',
        content: '<path d="" />',
        viewBox: '0 0 512 512',
      };
    }

    const defaultViewBoxSize: number = 512;
    const heightIndex: number = 1;
    const pathIndex: number = 4;

    const width: number = (icon.icon[0] as number) || defaultViewBoxSize;
    const height: number =
      (icon.icon[heightIndex] as number) || defaultViewBoxSize;
    const pathData: string = (icon.icon[pathIndex] as string) || '';

    return {
      name: icon.iconName || 'icon',
      content: `<path d="${pathData}" />`,
      viewBox: `0 0 ${width} ${height}`,
    };
  }

getReferenceData(): void {
    combineLatest([this.referenceStore.sessionType$])
      .subscribe(
        ([sessionTypes]: [IReferenceData[]]) => {
          this.sessionTypes = sessionTypes.filter(type => type.id !== 1);

          if (this.scheduledMeetingData) {
            this.scheduledMeetingData.sessionType = this.fixSessionTypeInValue(
              this.scheduledMeetingData.sessionType
            );
          }

          if (this.isScheduledTab) {
            this.patchForm(this.scheduledMeetingData);
          } else {
            this.patchForm(this.actualMeetingData);
          }
        },
        (error) => {
          this.laodingService.hide();
        }
      );
  }
  /**
   * Combines the selected date with the time from a time picker.
   * This ensures that when the date is changed, the time components remain correct.
   * @param date - The date selected in the date picker
   * @param time - The time selected in the time picker (which may contain an old date)
   * @returns Formatted ISO string with the selected date and time, or null if either is missing
   */
  private combineDateTime(date: Date | null, time: Date | null): string | null {
    if (!date || !time) return null;
    
    const pad: (n: number) => string = (n: number) =>
      n < 10 ? '0' + n : '' + n;
    
    // Use date from the date picker and time from the time picker
    return (
      date.getFullYear() +
      '-' +
      pad(date.getMonth() + 1) +
      '-' +
      pad(date.getDate()) +
      'T' +
      pad(time.getHours()) +
      ':' +
      pad(time.getMinutes()) +
      ':00'
    );
  }
  submitCertify(): void {
    ///
    const startDate: string = moment(this.indirectServiceForm.value.startDate).local().format('YYYY-MM-DDTHH:mm:ss') + 'Z'
    const startTime: string =
    this.combineDateTime(
      this.indirectServiceForm.value.startDate,
      this.indirectServiceForm.value.startTime
    ) + 'Z';

    const endTime: string =
      this.combineDateTime(
      this.indirectServiceForm.value.startDate,
      this.indirectServiceForm.value.endTime
     ) + 'Z';
    const meetingDetails = {
      scheduleMeetingData: !this.encounterSk ? this.scheduledMeetingData : this.indirectServiceMeetingDetails,
      actualMeetingData: { ...this.indirectServiceForm.value, status: 4, actualServiceDate:startDate, actualStartTime: startTime, actualEndTime: endTime, startDate: startDate, startTime: startTime, endTime: endTime },
      providerSk: this.providerSk,
    };
    const payload: IndirectMeetingRequest = new IndirectMeetingRequest({
      MeetingSk: this.meetingSk,
      formJson: JSON.stringify(meetingDetails),
      providerSk: this.providerSk,
      actualServiceDate: startDate,
      actualStartTime: startTime,
      actualEndTime: endTime,
    });
    this.encounterService.updateIndirectMeeting(payload).subscribe({
      next: () => {
        this.notificationService.show({
          content: 'Submitted Successfully',
          cssClass: 'notification-success',
          hideAfter: dialogTimer,
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: dialogAnimationDuration },
          type: { style: 'success', icon: true },
        });
        this.dialog.close({submitted: true,
          encounterSk: this.encounterSk,
          newStatus: 'Certified',
          meetingSk: this.meetingSk
        });
      },
      error: () => {
        this.notificationService.show({
          content: 'Submission Failed.',
          cssClass: 'notification-error',
          position: { horizontal: 'center', vertical: 'top' },
          animation: { type: 'slide', duration: 60 },
          type: { style: 'error', icon: false },
        });
      },
    });
  } 
}
